import { type AclUserCredential, type GuardStatus, UserCredentials } from '@capsicum/common';

import { useCredentialsQuery } from '../data-access';
import {
	type CredentialHandlerDeniedReason,
	GENERIC_HANDLER_DENIED_REASONS,
	LOCATION_HANDLER_DENIED_REASONS,
	REQUIREMENT_DENIED_HANDLER_DENIED_REASONS,
	REQUIREMENT_NOT_COMPLETED_HANDLER_DENIED_REASONS,
	USER_HANDLER_DENIED_REASONS,
	UserCredentialsDeniedReason,
	UserCredentialsStatus
} from '../models';

const TEMPORARY_GENERIC_DENIED_REASONS = [
	...GENERIC_HANDLER_DENIED_REASONS,
	...LOCATION_HANDLER_DENIED_REASONS,
	...USER_HANDLER_DENIED_REASONS
];

/**
 * Hook that checks either one or a list of credentials and returns its status and the denied reason of the first one.
 * It also checks the requirement submission credential as it's needed for the user to be able to submit requirements.
 *
 * @param {UserCredentialsCode|UserCredentialsCode[]} params.credentialCodes - Refers to either a unique credential code or an array of credential codes to check.
 * @returns {Object} An object containing the following properties:
 * - `deniedReason`: The denied reason if there's any for the first credential on the list.
 * - `deniedRequirements`: The denied requirements identifiers.
 * - `firstMissingRequirement`: The first missing requirement is the credential is denied by that reason.
 * - `status`: Allowed if all credentials are granted, loading if credentials are fetching or denied if any of them is denied.
 */
export const useCredentials = (credentialCodes: Array<string> | string) => {
	const { areCredentialsFetching, credentials, forcedRequirementsIdentifiers } = useCredentialsQuery();

	const getCredential = (credentialCodeToCheck: string) =>
		credentials?.find(credential => credential.code === credentialCodeToCheck) as AclUserCredential;

	const credentialsToCheck = Array.isArray(credentialCodes) ? credentialCodes : [credentialCodes];

	const isCredentialDenied = (credential: AclUserCredential) => credential?.status !== UserCredentialsStatus.GRANTED;

	const isRequirementSubmissionDenied = isCredentialDenied(getCredential(UserCredentials.REQUIREMENT_SUBMISSION));

	const deniedCredentials = credentialsToCheck
		.map(credentialCodeToCheck => getCredential(credentialCodeToCheck))
		.filter(isCredentialDenied);

	const status: GuardStatus = areCredentialsFetching ? 'loading' : !deniedCredentials.length ? 'allowed' : 'denied';

	const firstCredentialDeniedReasons = deniedCredentials[0]?.reasons?.map(reason => reason.code as string) || [];

	const deniedReason = ((): CredentialHandlerDeniedReason => {
		if (status !== 'denied') return undefined;

		if (TEMPORARY_GENERIC_DENIED_REASONS.some(value => firstCredentialDeniedReasons.includes(value))) {
			return 'GENERIC_DENIED_REASON';
		} else if (
			REQUIREMENT_DENIED_HANDLER_DENIED_REASONS.some(value => firstCredentialDeniedReasons.includes(value))
		) {
			return 'REQUIREMENT_DENIED_REASON';
		} else if (isRequirementSubmissionDenied) {
			return 'GENERIC_DENIED_REASON';
		} else if (
			REQUIREMENT_NOT_COMPLETED_HANDLER_DENIED_REASONS.some(value => firstCredentialDeniedReasons.includes(value))
		) {
			return 'REQUIREMENT_NOT_COMPLETED_REASON';
		}
	})();

	const firstMissingRequirement = deniedCredentials[0]?.reasons?.find(
		reason => reason.code === UserCredentialsDeniedReason.NO_GRANT_USER_REQUIREMENT_NOT_COMPLETED
	)?.requirementIdentifiers?.[0];

	const deniedRequirements = deniedCredentials[0]?.reasons?.find(
		reason => reason.code === UserCredentialsDeniedReason.NO_GRANT_USER_REQUIREMENT_DENIED
	)?.requirementIdentifiers;

	return {
		deniedReason,
		deniedRequirements,
		firstMissingRequirement,
		isForcedRequirement: Boolean(
			firstMissingRequirement && firstMissingRequirement === forcedRequirementsIdentifiers?.[0]
		),
		status
	};
};
